import React, { useRef, useState } from 'react';
import { Upload, X, Loader2, FileImage, FileText } from 'lucide-react';
import { uploadWebsiteBlockImage } from '../../../../services/storageService';
import { uploadDownloadFile, deleteDownloadFile } from '../../../../services/storageService';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileEmbedEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const { content } = block;
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    setIsUploading(true);
    setUploadError('');

    try {
      const newFiles = [];

      for (const file of selectedFiles) {
        const isImage = file.type.startsWith('image/');
        const isPdf = file.type === 'application/pdf';

        if (!isImage && !isPdf) continue;

        let url;
        let fileSize = file.size;
        let fileType = isPdf ? 'pdf' : file.type.split('/')[1];

        if (isImage) {
          url = await uploadWebsiteBlockImage(file, profileId, block.id);
        } else {
          const result = await uploadDownloadFile(file, profileId, block.id);
          url = result.url;
          fileSize = result.fileSize;
          fileType = result.fileType;
        }

        newFiles.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          url,
          fileName: file.name,
          fileType,
          fileSize,
        });
      }

      if (newFiles.length) {
        handleChange('files', [...(content.files || []), ...newFiles]);
      }
    } catch (error) {
      setUploadError(error.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = async (fileToRemove) => {
    // Delete PDFs from storage; images are managed by block image cleanup
    if (fileToRemove.fileType === 'pdf' && fileToRemove.url) {
      try {
        await deleteDownloadFile(fileToRemove.url);
      } catch (err) {
        console.error('Failed to delete file from storage:', err);
      }
    }
    handleChange(
      'files',
      (content.files || []).filter((f) => f.id !== fileToRemove.id)
    );
  };

  const files = content.files || [];

  return (
    <div className="space-y-4">
      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Heading (optional)
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="e.g. Tournament Flyer"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Upload Button */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Files
        </label>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="w-full h-24 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-700 rounded-lg hover:border-[#749555] transition-colors"
        >
          {isUploading ? (
            <Loader2 size={24} className="text-[#749555] animate-spin" />
          ) : (
            <>
              <Upload size={20} className="text-gray-500" />
              <span className="text-sm text-gray-400">
                Upload images or PDFs
              </span>
              <span className="text-xs text-gray-600">
                Images up to 5MB, PDFs up to 25MB
              </span>
            </>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          multiple
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>

      {/* Upload Error */}
      {uploadError && (
        <p className="text-red-400 text-sm">{uploadError}</p>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file) => {
            const isPdf = file.fileType === 'pdf';
            return (
              <div
                key={file.id}
                className="flex items-center gap-3 p-3 bg-[#1a1a2e] border border-gray-700 rounded-lg"
              >
                {/* Thumbnail / Icon */}
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center bg-gray-800">
                  {isPdf ? (
                    <FileText size={24} className="text-red-400" />
                  ) : (
                    <img
                      src={file.url}
                      alt={file.fileName}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Info */}
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white font-medium truncate">
                    {file.fileName}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span
                      className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                        isPdf
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-blue-500/20 text-blue-400'
                      }`}
                    >
                      {file.fileType}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatFileSize(file.fileSize)}
                    </span>
                  </div>
                </div>

                {/* Delete */}
                <button
                  onClick={() => handleRemoveFile(file)}
                  className="p-1.5 text-gray-400 hover:text-red-400 transition-colors flex-shrink-0"
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Style Panels */}
      {!hideStylePanels && (
        <>
          <SizeSpacingPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={BLOCK_FEATURES['file-embed']}
          />
          <ColorsFontsPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={BLOCK_FEATURES['file-embed']}
            theme={theme || {}}
            blockType="file-embed"
          />
        </>
      )}
    </div>
  );
};

export default FileEmbedEditor;
